import React from 'react';
import { Loader2 } from 'lucide-react';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * DashboardWidget Component
 * A premium, glassmorphic container for dashboard content with permission control.
 * 
 * @param {string} title - Widget title
 * @param {React.ReactNode} icon - Widget icon from lucide-react
 * @param {React.ReactNode} children - Widget content
 * @param {string} className - Additional CSS classes
 * @param {React.ReactNode} headerAction - Optional action element in the header (e.g., a button)
 * @param {boolean} isLoading - Loading state
 * @param {string[]} allowedPermissions - Array of permission strings required to see this widget
 * @param {string[]} allowedRoles - Array of roles allowed to see this widget
 */
const DashboardWidget = ({
    title,
    icon,
    children,
    className = "",
    headerAction,
    isLoading = false,
    allowedPermissions = [],
    allowedRoles = []
}) => {
    const { hasPermission, hasAnyRole, isRoot } = usePermissions();

    // Permission check logic
    const canSee = React.useMemo(() => {
        if (isRoot) return true;
        if (allowedPermissions.length === 0 && allowedRoles.length === 0) return true;

        const hasPerm = allowedPermissions.length > 0
            ? allowedPermissions.every(p => hasPermission(p))
            : true;

        const hasRol = allowedRoles.length > 0
            ? hasAnyRole(allowedRoles)
            : true;

        return hasPerm && hasRol;
    }, [allowedPermissions, allowedRoles, hasPermission, hasAnyRole, isRoot]);

    if (!canSee) return null;

    return (
        <div className={`
            relative group overflow-hidden
            bg-[#0f1115]/40 backdrop-blur-xl
            border border-white/[0.06] rounded-[2rem]
            transition-all duration-300 hover:bg-[#0f1115]/50
            hover:border-white/[0.1] hover:shadow-[0_20px_40px_-15px_rgba(0,0,0,0.5)]
            ${className}
        `}>
            {/* Header */}
            {(title || icon || headerAction) && (
                <div className="flex items-center justify-between px-7 py-6 border-b border-white/[0.04]">
                    <div className="flex items-center gap-3.5">
                        {icon && (
                            <div className="p-2 rounded-xl bg-white/[0.03] text-gray-400 group-hover:text-[#fd7000] transition-colors duration-300">
                                {React.cloneElement(icon, { size: 18 })}
                            </div>
                        )}
                        {title && (
                            <h3 className="text-[15px] font-bold text-gray-200 tracking-wide uppercase">
                                {title}
                            </h3>
                        )}
                    </div>
                    {headerAction && (
                        <div className="flex items-center">
                            {headerAction}
                        </div>
                    )}
                </div>
            )}

            {/* Content Area */}
            <div className="relative p-7">
                {isLoading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 text-[#fd7000] animate-spin mb-3 opacity-80" />
                        <span className="text-sm text-gray-500 font-medium">Yükleniyor...</span>
                    </div>
                ) : (
                    <div className="animate-fadeIn">
                        {children}
                    </div>
                )}
            </div>

            {/* Subtle Gradient Glow */}
            <div className="absolute -inset-1 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
        </div>
    );
};

export default DashboardWidget;
